/**
 * Perfil → Utilizações: consumo de IA da empresa no mês corrente.
 * O teto vem do plano (`ai_token_limit`); os tokens são medidos em toda
 * chamada à OpenAI (`utils/ai_usage.py` no backend).
 */
import { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import api from "../../utils/api";

const fmt = (n) => String(n || 0).replace(/\B(?=(\d{3})+(?!\d))/g, ".");

const MESES = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

const mesLabel = (periodo) => {
  const [ano, mes] = (periodo || "").split("-");
  const nome = MESES[Number(mes) - 1];
  if (!ano || !nome) return periodo || "—";
  return `${nome} de ${ano}`;
};

export default function AiUsageTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/ai-usage/summary")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center" data-testid="ai-usage-loading">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex-1 px-6 py-6" data-testid="ai-usage-empty">
        <p className="text-sm text-gray-500">Não foi possível carregar o consumo agora.</p>
      </div>
    );
  }

  if (!data.modulo_ativo) {
    return (
      <div className="flex-1 px-6 py-6" data-testid="ai-usage-sem-modulo">
        <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-4">
          <p className="text-sm font-medium text-gray-800">Sem recursos de IA no plano</p>
          <p className="text-xs text-gray-500 mt-1">
            As funções de inteligência artificial não fazem parte do plano desta empresa,
            então não há consumo para exibir.
          </p>
        </div>
      </div>
    );
  }

  const pct = Math.min(data.percentual || 0, 100);
  const cor = pct >= 100 ? "#ef4444" : pct >= 80 ? "#f59e0b" : "#10b981";
  const usos = (data.usos || []).filter((u) => u.tokens > 0);
  const maxUso = usos[0]?.tokens || 1;
  const maxHist = Math.max(...(data.historico || []).map((h) => h.tokens), 1);

  return (
    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-testid="ai-usage-tab">
      <div className="rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">
              Inteligência artificial · <span className="first-letter:uppercase">{mesLabel(data.periodo)}</span>
            </p>
            <p className="text-3xl font-semibold text-gray-900 mt-1.5" data-testid="ai-usage-tokens">
              {fmt(data.tokens)}
              <span className="text-sm font-normal text-gray-400">
                {" "}
                / {data.limite ? fmt(data.limite) : "ilimitado"} tokens
              </span>
            </p>
          </div>
          <span
            className="text-xs font-bold px-2.5 py-1 rounded-full text-white shrink-0"
            style={{ backgroundColor: cor }}
            data-testid="ai-usage-percent"
          >
            {data.limite ? `${data.percentual}%` : "sem teto"}
          </span>
        </div>

        {data.limite ? (
          <>
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden mt-4">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, backgroundColor: cor }}
                data-testid="ai-usage-bar"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2.5">
              {data.bloqueado ? (
                <span className="text-red-600 font-medium">
                  Limite atingido. As funções de IA acionadas pelos usuários voltam no dia 1º.
                </span>
              ) : (
                <>Restam {fmt(data.restante)} tokens neste ciclo. O saldo renova no dia 1º.</>
              )}
            </p>
          </>
        ) : null}

        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-100">
          {[
            ["Chamadas", fmt(data.chamadas)],
            ["Tokens de entrada", fmt(data.prompt_tokens)],
            ["Tokens de resposta", fmt(data.completion_tokens)],
          ].map(([label, valor]) => (
            <div key={label}>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">
                {label}
              </p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5">{valor}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-2">
          Onde foi usado neste mês
        </p>
        {usos.length === 0 ? (
          <p className="text-sm text-gray-400">Nenhum uso de IA registrado neste mês.</p>
        ) : (
          <ul className="space-y-3" data-testid="ai-usage-breakdown">
            {usos.map((u) => (
              <li key={u.key}>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm font-medium text-gray-800 truncate">{u.label}</p>
                  <p className="text-xs text-gray-500 shrink-0">
                    {fmt(u.tokens)} · {u.chamadas} chamada(s)
                  </p>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1.5">
                  <div
                    className="h-full rounded-full bg-gray-800"
                    style={{ width: `${(u.tokens / maxUso) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">{u.onde}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {(data.historico || []).length > 1 ? (
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-2">
            Histórico mensal
          </p>
          <ul className="space-y-2" data-testid="ai-usage-history">
            {data.historico.map((h) => (
              <li key={h.periodo} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-28 shrink-0 first-letter:uppercase">
                  {mesLabel(h.periodo)}
                </span>
                <span className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <span
                    className="block h-full rounded-full bg-gray-300"
                    style={{ width: `${(h.tokens / maxHist) * 100}%` }}
                  />
                </span>
                <span className="text-xs text-gray-600 w-24 text-right shrink-0">
                  {fmt(h.tokens)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="text-xs text-gray-400 flex items-start gap-1.5">
        <Sparkles size={13} className="mt-0.5 shrink-0 text-gray-300" />
        O consumo é da empresa inteira e soma todos os usos de IA do sistema. Rotinas
        automáticas (parecer da OS, explicação de alerta) continuam rodando mesmo após o
        limite; as funções acionadas por um usuário ficam indisponíveis até o próximo ciclo.
      </p>
    </div>
  );
}
